import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui';
import { StatusIndicator } from '@/components/domain';
import { BrandMark } from '@/components/shared/BrandMark';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { useAuthStore, useMonitoringStore, useWebSocketStore, useUIStore } from '@/stores';
import { cn } from '@/utils/cn';
import {
  cameraActiveSuccessMessage,
  getStudentPrepReadiness,
  shouldSurfaceMonitoringError,
} from '@/monitoring/studentReadiness';

interface CameraStatus {
  active: boolean;
  fps: number;
  error?: string;
}

export const StudentInterface: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { 
    startMonitoring, 
    stopMonitoring, 
    isMonitoring, 
    gazeData,
    connect: connectMonitoring,
    processedFrame,
    error: monitoringError,
    setError: setMonitoringError,
  } = useMonitoringStore();
  const { isConnected, connect: connectWebSocket } = useWebSocketStore();
  const { showNotification } = useUIStore();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>({ active: false, fps: 0 });
  const [cameraInitialized, setCameraInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;
  const lastLogTime = useRef<number>(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigationHandled = useRef<boolean>(false);
  const websocketConnected = useRef<boolean>(false);
  const isMonitoringRef = useRef(isMonitoring);

  useEffect(() => {
    isMonitoringRef.current = isMonitoring;
  }, [isMonitoring]);

  // Drop stale ETH-disable errors left from older builds / WS frames
  useEffect(() => {
    if (monitoringError && !shouldSurfaceMonitoringError(monitoringError)) {
      setMonitoringError(null);
    }
  }, [monitoringError, setMonitoringError]);

  // Check authentication and consent - run only once with debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // Prevent multiple navigation attempts
    if (navigationHandled.current) {
      return;
    }
    
    const checkAuth = () => {
      if (!isAuthenticated || !user) {
        navigationHandled.current = true;
        timeoutId = setTimeout(() => navigate('/login', { replace: true }), 100);
        return false;
      }

      const consentGiven = localStorage.getItem('consent_given');
      if (consentGiven !== 'true') {
        navigationHandled.current = true;
        timeoutId = setTimeout(() => navigate('/consent', { replace: true }), 100);
        return false;
      }
      return true;
    };
    
    const authValid = checkAuth();
    
    // Establish WebSocket connection if authenticated (prevent duplicates)
    if (authValid && user?.sessionId && !websocketConnected.current) {
      console.log('🔌 Establishing WebSocket connection for session:', user.sessionId);
      websocketConnected.current = true;
      
      // Add a small delay to ensure backend is ready
      setTimeout(() => {
        connectWebSocket(user.sessionId).then(() => {
          console.log('✅ WebSocket connection established successfully');
        }).catch((error) => {
          console.error('❌ WebSocket connection failed:', error);
          websocketConnected.current = false; // Reset on failure
          showNotification({
            type: 'warning',
            title: 'Connection Retrying',
            message: 'Attempting to establish real-time connection...'
          });
        });
      }, 500);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      // Do NOT disconnect WebSocket here — ExamInterface mounts before this
      // cleanup runs, and a deferred disconnect kills the exam connection
      // (calibration freezes mid-progress with "Waiting for camera connection").
      websocketConnected.current = false;
    };
  }, []); // Remove dependencies to prevent navigation loops

  // Initialize camera and monitoring - wait for video element to be ready
  useEffect(() => {
    let timer: NodeJS.Timeout;
    let mounted = true;
    
    const initCamera = async () => {
      if (!mounted || cameraInitialized || cameraStatus.active || retryCount >= maxRetries) {
        return;
      }
      
      timer = setTimeout(() => {
        if (mounted) {
          initializeCamera();
        }
      }, 500);
    };
    
    initCamera();
    
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
      cleanup();
    };
  }, []); // Only run once on mount

  // Start monitoring when camera is ready and WebSocket connected
  useEffect(() => {
    console.log('🔍 Monitoring check - Camera:', cameraStatus.active, 'WebSocket:', isConnected, 'SessionId:', !!user?.sessionId);
    
    if (cameraStatus.active && isConnected && user?.sessionId) {
      console.log('🎯 Camera ready — on-device WebGazer calibrates on the exam screen');
      startMonitoring();
      connectMonitoring(user.sessionId);
      stopFrameCapture();
    } else {
      stopFrameCapture();
    }
  }, [cameraStatus.active, isConnected, user?.sessionId, startMonitoring, connectMonitoring]);

  // Gaze updates arrive via monitoring store; focus/behavior read from currentData in UI
  useEffect(() => {
    if (gazeData) {
      const now = Date.now();
      if (now - lastLogTime.current > 5000) {
        console.log('👁️ Gaze data received:', gazeData);
        lastLogTime.current = now;
      }
    }
  }, [gazeData]);

  const initializeCamera = async () => {
    // Prevent multiple initialization attempts
    if (cameraInitialized || cameraStatus.active) {
      console.log('🚫 Camera already initialized, skipping...');
      return;
    }

    // Check retry limit
    if (retryCount >= maxRetries) {
      console.error('❌ Max retry attempts reached, giving up');
      setCameraStatus({ active: false, fps: 0, error: 'Video element not available after multiple retries' });
      showNotification({
        type: 'error',
        title: 'Camera Error',
        message: 'Failed to initialize camera after multiple attempts. Please refresh the page.'
      });
      return;
    }

    console.log(`🎥 Initializing camera (attempt ${retryCount + 1}/${maxRetries})...`);
    setCameraInitialized(true);
    
    try {
      // Check if video element is available
      if (!videoRef.current) {
        console.error('❌ Video ref is null, retrying in 500ms...');
        setCameraInitialized(false);
        setRetryCount(prev => prev + 1);
        // Use requestAnimationFrame instead of setTimeout for better performance
        requestAnimationFrame(() => {
          setTimeout(() => initializeCamera(), 500);
        });
        return;
      }

      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported');
      }

      console.log('📹 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });

      console.log('✅ Camera stream obtained:', stream);

      if (videoRef.current) {
        console.log('🔗 Setting video srcObject...');
        videoRef.current.srcObject = stream;
        
        // Set camera active immediately when stream is obtained
        setCameraStatus({ active: true, fps: 30 });
        setRetryCount(0); // Reset retry count on success
        
        console.log('▶️ Starting video playback...');
        try {
          await videoRef.current.play();
          console.log('✅ Video playing successfully');
          
          showNotification({
            type: 'success',
            title: 'Camera Active',
            message: cameraActiveSuccessMessage(),
          });
        } catch (playError) {
          console.error('❌ Video play failed:', playError);
          // Keep camera active even if autoplay fails - user can click play
        }
      } else {
        console.error('❌ Video ref became null during initialization');
        throw new Error('Video element not available');
      }
    } catch (error) {
      console.error('❌ Camera initialization failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setCameraStatus({ active: false, fps: 0, error: errorMessage });
      setCameraInitialized(false); // Allow retry
      
      showNotification({
        type: 'error',
        title: 'Camera Error',
        message: `Failed to access camera: ${errorMessage}`
      });
    }
  };

  const stopFrameCapture = () => {
    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
  };

  // startFrameCapture removed — gaze uses WebGazer on the exam screen


  const cleanup = () => {
    stopFrameCapture();
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    stopMonitoring();
  };

  const handleStartExam = () => {
    if (!cameraStatus.active) {
      showNotification({
        type: 'error',
        title: 'Camera Required',
        message: 'Please ensure camera is active before starting exam'
      });
      return;
    }

    // Use setTimeout to prevent navigation throttling
    setTimeout(() => {
      navigate('/exam');
    }, 100);
  };

  const prep = getStudentPrepReadiness({
    active: cameraStatus.active,
    error: cameraStatus.error,
  });
  const isReady = prep.isReady;
  const readinessLabel = prep.readinessLabel;
  const visibleMonitoringError = shouldSurfaceMonitoringError(monitoringError)
    ? monitoringError
    : null;

  return (
    <div className="min-h-screen bg-surface-canvas font-sans text-content-primary">
      <nav className="sticky top-0 z-50 border-b border-subtle bg-surface-canvas/95">
        <div className="mx-auto flex max-w-page flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <BrandMark />
            <div className="min-w-0">
              <h1 className="truncate font-display text-heading-sm font-normal">
                Exam readiness
              </h1>
              <p className="text-sm text-content-muted">Confirm camera and monitoring before you start</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="space-y-8">
          <section className="overflow-hidden rounded-card border border-hairline bg-surface-card">
            <div className="flex items-center justify-between gap-3 border-b border-subtle px-4 py-3 sm:px-5">
              <div>
                <p className="text-caption font-medium uppercase tracking-[0.14em] text-content-muted">
                  Step 1
                </p>
                <h2 className="mt-0.5 font-display text-lg font-normal">Camera check</h2>
              </div>
              <StatusIndicator
                status={cameraStatus.active ? 'nominal' : cameraStatus.error ? 'critical' : 'connecting'}
                label={cameraStatus.active ? 'Nominal' : cameraStatus.error ? 'Critical' : 'Connecting'}
                pulse={!cameraStatus.active}
              />
            </div>
            <div className="relative aspect-video bg-obsidian">
              <video
                ref={videoRef}
                className={cn(
                  'h-full w-full object-cover',
                  cameraStatus.active ? 'block' : 'hidden',
                  processedFrame ? 'opacity-0 absolute inset-0 pointer-events-none' : ''
                )}
                autoPlay
                muted
                playsInline
              />
              {cameraStatus.active && processedFrame && (
                <img
                  src={`data:image/jpeg;base64,${processedFrame}`}
                  alt="Camera monitoring preview"
                  className="h-full w-full object-cover"
                />
              )}
              <canvas ref={canvasRef} className="hidden" width={640} height={480} />
              {!cameraStatus.active && (
                <div className="absolute inset-0 flex items-center justify-center p-5 text-center text-paper-white">
                  <div className="max-w-sm">
                    {cameraStatus.error ? (
                      <Camera className="mx-auto mb-4 size-10 opacity-70" aria-hidden="true" />
                    ) : (
                      <div className="mx-auto mb-4 size-10 animate-spin rounded-full border-4 border-mist border-t-paper-white" />
                    )}
                    <p className="font-display text-lg font-normal">
                      {cameraStatus.error ? 'Camera unavailable' : 'Starting camera'}
                    </p>
                    <p className="mt-1 text-sm text-paper-white/70">
                      {cameraStatus.error
                        ? 'Check browser permissions, then retry.'
                        : 'Allow camera access when prompted'}
                    </p>
                    {cameraStatus.error && (
                      <div className="mt-4 rounded-control border border-state-critical bg-state-critical-soft p-3">
                        <p className="text-sm text-state-critical">{cameraStatus.error}</p>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            setCameraInitialized(false);
                            setCameraStatus({ active: false, fps: 0 });
                            setRetryCount(0);
                            setTimeout(() => initializeCamera(), 100);
                          }}
                          className="mt-2"
                        >
                          <RefreshCw className="size-4" aria-hidden="true" />
                          Retry Camera
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="rounded-card border border-hairline bg-surface-card px-4 py-5 sm:px-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-caption font-medium uppercase tracking-[0.14em] text-content-muted">
                  Step 2
                </p>
                <h2 className="mt-0.5 font-display text-lg font-normal">{prep.step2Title}</h2>
                <p className="mt-2 text-sm text-content-secondary">{prep.step2Hint}</p>
              </div>
              <StatusIndicator
                status={prep.statusKind}
                label={prep.statusLabel}
                pulse={!isReady}
              />
            </div>
            <dl className="mt-5 grid gap-3 border-t border-subtle pt-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-content-muted">Readiness</dt>
                <dd className="mt-1 text-sm font-medium text-content-primary">{readinessLabel}</dd>
              </div>
              <div>
                <dt className="text-xs text-content-muted">Camera</dt>
                <dd className="mt-1 text-sm font-medium text-content-primary">
                  {cameraStatus.active ? 'Active' : cameraStatus.error ? 'Unavailable' : 'Starting…'}
                </dd>
              </div>
            </dl>
            {visibleMonitoringError && (
              <p className="mt-4 text-sm text-state-critical">{visibleMonitoringError}</p>
            )}
          </section>

          <section>
            <Button
              variant="primary"
              size="lg"
              onClick={handleStartExam}
              disabled={!isReady}
              className="w-full"
            >
              {prep.startButtonLabel}
            </Button>
            <p className="mt-3 text-center text-sm text-content-muted">
              Keep your face visible during the exam. Gaze stays on your device (WebGazer).
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};
